/**
 * Lead validation and tiering.
 *
 * Tiering is computed SERVER-SIDE only (build prompt §8) — it lives in shared/
 * for testability, but the browser never calls it and never sees the result.
 */

import { EMPLOYEE_BUCKETS, FREE_MAIL_DOMAINS, INDUSTRIES } from './config.js';
import type { EmployeeBucketId, LeadTier, RoleId } from './types.js';

/* ------------------------------------------------------------------ *
 * Email
 * ------------------------------------------------------------------ */

/** Pragmatic RFC-5322 subset: good enough to reject typos, not a parser. */
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export function isValidEmailFormat(email: string): boolean {
  const trimmed = email.trim();
  return trimmed.length <= 254 && EMAIL_RE.test(trimmed);
}

export function domainOf(email: string): string {
  return email.trim().toLowerCase().split('@')[1] ?? '';
}

export function isFreeMail(email: string): boolean {
  return FREE_MAIL_DOMAINS.includes(domainOf(email));
}

export type EmailRejection = 'format' | 'free-mail';

/** `null` means the address is acceptable. */
export function checkBusinessEmail(email: string): EmailRejection | null {
  if (!isValidEmailFormat(email)) return 'format';
  if (isFreeMail(email)) return 'free-mail';
  return null;
}

/* ------------------------------------------------------------------ *
 * Tiering
 * ------------------------------------------------------------------ */

export function isRegulatedIndustry(industryId: string): boolean {
  return INDUSTRIES.find((i) => i.id === industryId)?.regulated ?? false;
}

function bucketBounds(id: EmployeeBucketId): { min: number; max: number } {
  const bucket = EMPLOYEE_BUCKETS.find((b) => b.id === id);
  if (!bucket) throw new Error(`Unknown employee bucket ${id}`);
  return { min: bucket.min, max: bucket.max };
}

export interface TierInput {
  role: RoleId;
  industry: string;
  employees: EmployeeBucketId;
  overallScore: number;
}

/**
 * Tier A — sales-ready:      role in {C-level, Architect} AND >=500 employees
 *                            AND regulated industry AND score < 50.
 * Tier B — practice nurture: >=100 employees AND score < 60 (and not Tier A).
 * Tier C — long-term nurture: everything else.
 *
 * DEVIATION FROM THE ORIGINAL BRIEF, agreed 2026-07-19. The brief specified
 * Tier B as "100-999 employees", which the brief itself flagged as an open
 * question. That ceiling meant Tier B could never match an organisation of
 * 1,000+, so any large lead that missed even one of Tier A's four conditions
 * dropped straight to Tier C — the lowest priority. A 600-person retailer
 * scoring 55 outranked a 5,000-person bank scoring 55, which inverts the
 * commercial intent.
 *
 * Removing the upper bound is the minimal fix: large organisations that miss
 * Tier A now land in B rather than C. Tier A is unchanged, so nothing is
 * promoted into sales-ready that was not already there.
 */
export function tierFor({ role, industry, employees, overallScore }: TierInput): LeadTier {
  const { min } = bucketBounds(employees);

  const isTierA =
    (role === 'c-level' || role === 'architect') &&
    min >= 500 &&
    isRegulatedIndustry(industry) &&
    overallScore < 50;
  if (isTierA) return 'A';

  const isTierB = min >= 100 && overallScore < 60;
  if (isTierB) return 'B';

  return 'C';
}

export const TIER_LABELS: Record<LeadTier, string> = {
  A: 'Tier A · sales-ready',
  B: 'Tier B · practice nurture',
  C: 'Tier C · long-term nurture',
};
